import { PillProps } from 'ext-ui/components/Pill/Pill'
import {
  base_colorAction,
  base_colorContentBorder,
  tag_colorTagBlueBackground,
  tag_colorTagBlueText,
  tag_colorTagDefaultBackground,
  tag_colorTagDefaultText,
  tag_colorTagHighlightBackground,
  tag_colorTagHighlightText,
  tag_colorTagPendingBackground,
  tag_colorTagPendingTextHover,
  tag_colorTagSevereBackground,
  tag_colorTagSevereText,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  font_family_weight_regular,
  font_md,
  font_sm,
  fontH1,
  fontH3,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

export const wrapper = css`
  display: flex;
  flex-direction: column;
`

export const cardholderName = css`
  ${fontH3};
  color: ${text_colorTextPrimary};
  margin: 0;
`

export const metaText = css`
  ${font_md};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
`

/* ---- identity lockup v2 (Design VQA round 2, slice B) ---- */

export const lockupColumn = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(2)};
`

export const titleRow = css`
  display: flex;
  align-items: center;
  gap: ${spacing(2.5)};
  flex-wrap: wrap;
  row-gap: ${spacing(2)};
  min-width: 0;
`

export const personGroup = css`
  display: flex;
  align-items: center;
  gap: ${spacing(2.5)};
  min-width: 0;
`

/**
 * Tint by `StatusPill`'s own colour mapping (`statusPillColor`), reused rather
 * than re-derived so the hero and the pill can never disagree on what a status
 * means. `undefined` (CANCELED) falls back to the neutral default tag colours.
 */
const statusUnitTint: Partial<
  Record<NonNullable<PillProps['color']>, ReturnType<typeof css>>
> = {
  highlight: css`
    background-color: ${tag_colorTagHighlightBackground};
    color: ${tag_colorTagHighlightText};
  `,
  pending: css`
    background-color: ${tag_colorTagPendingBackground};
    color: ${tag_colorTagPendingTextHover};
  `,
  saas: css`
    background-color: ${tag_colorTagBlueBackground};
    color: ${tag_colorTagBlueText};
  `,
  severe: css`
    background-color: ${tag_colorTagSevereBackground};
    color: ${tag_colorTagSevereText};
  `,
}

const statusUnitDefaultTint = css`
  background-color: ${tag_colorTagDefaultBackground};
  color: ${tag_colorTagDefaultText};
`

/**
 * The joined status + last-four unit. Only the tint (background/text colour)
 * varies by state — the glyph and label inherit it via `currentColor`/`inherit`.
 * The divider and last four are recoloured explicitly below (§7.1 of the design
 * VQA): `currentColor` here would tint the last four with the status colour,
 * which turns a constant fact (the card's last four) into a false status
 * signal.
 */
export const statusUnit = (tint: PillProps['color']) => css`
  display: inline-flex;
  align-items: center;
  gap: ${spacing(2)};
  flex: 0 0 auto;
  padding: ${spacing(1)} ${spacing(2.5)};
  border-radius: ${spacing(2)};
  white-space: nowrap;
  ${(tint !== undefined && statusUnitTint[tint]) || statusUnitDefaultTint}
`

/* Tinted with the unit (currentColor, inherited) — unlike the divider and last
   four below, the glyph is part of the status segment's own colour treatment.
   Pinned so it cannot be squeezed when the title row wraps at narrow widths. */
export const statusGlyph = css`
  flex: 0 0 auto;
`

export const statusLabel = css`
  ${font_md};
  font-family: ${font_family_weight_regular};
  color: inherit;
`

/* Explicitly NOT currentColor — stays neutral in every status tint. */
export const statusDivider = css`
  width: 1px;
  height: 12px;
  flex: 0 0 auto;
  background-color: ${text_colorTextSecondary};
`

/* Explicitly NOT currentColor — see statusUnit's doc comment. */
export const lastFour = css`
  ${font_md};
  /* Rubik faces all register at font-weight: normal, so the medium face is
     selected by FAMILY — a numeric font-weight gets faux-bold or nothing.
     Same convention as SpendLimitTile.style.ts. */
  font-family: ${font_family_weight_medium};
  /* No spacing/typography token exists at this value; matches the design's own CSS. */
  letter-spacing: 0.28px;
  color: ${text_colorTextPrimary};
  white-space: pre;
  font-variant-numeric: tabular-nums;
`

export const supportingRow = css`
  display: flex;
  align-items: center;
  gap: ${spacing(3.5)};
  flex-wrap: wrap;
  row-gap: ${spacing(1.5)};
  min-width: 0;
`

export const nicknameRow = css`
  display: flex;
  align-items: center;
  ${font_md};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
`

export const nicknameTrigger = css`
  display: inline-flex;
  align-items: center;
  gap: ${spacing(1)};
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  color: ${text_colorTextSecondary};
  cursor: pointer;

  @media (prefers-reduced-motion: no-preference) {
    transition: color 150ms ease;
  }

  &:hover,
  &:focus-visible {
    color: ${base_colorAction};
  }

  &:focus-visible {
    outline: 2px solid ${base_colorAction};
    outline-offset: 2px;
  }
`

/* ---- financial metric v2 (Design VQA round 2, slice C) ----
 * Internal rhythm is deliberately uneven (4/16/4/4px, not one uniform gap),
 * so gaps are explicit margins on the children rather than a container gap.
 * No width-scale token exists at 400px — the design flagged this as expected.
 */
export const creditBlock = css`
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  width: 400px;
  max-width: 100%;
  margin-top: ${spacing(8)};
`

export const balanceLabel = css`
  ${font_md};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
  margin: 0 0 ${spacing(1)};
`

/**
 * The dominant figure — the card balance, and only the card balance.
 *
 * The `limits === undefined` fallback deliberately does NOT use this rule; it
 * renders through `creditLimit` (secondary treatment) instead, because a
 * "not available"/loading message has no business being set at H1 scale. If a
 * second dominant figure ever appears, reuse this rule rather than forking a
 * parallel H1-sized one, or the two will drift apart.
 */
export const creditAmount = css`
  ${fontH1};
  color: ${text_colorTextPrimary};
  font-variant-numeric: tabular-nums;
  margin: 0 0 ${spacing(4)};
`

export const creditLimit = css`
  ${font_md};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
`

export const creditRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing(2)};
  flex-wrap: nowrap;
  white-space: nowrap;
  margin: 0 0 ${spacing(1)};
`

/* Same visual treatment as balanceLabel, but no margin of its own — creditRow
   owns the spacing below it. */
export const creditRowLabel = css`
  ${font_md};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
`

/**
 * Track colour matches the design's grey/450 (#DADBE0) — the design calls this
 * out explicitly: on a white header, a surface-coloured track reads as
 * invisible. `base_colorContentBorder` is the nearest existing semantic token
 * (it resolves to grey450 in the extend theme; raw palette colours are banned
 * in src/**, see EX-39864). Decorative only (`aria-hidden`); the figure it
 * visualises is stated in words in `remaining` directly below it.
 */
export const meterTrack = css`
  position: relative;
  height: 5px;
  border-radius: ${spacing(0.5)};
  background-color: ${base_colorContentBorder};
  overflow: hidden;
  margin: 0 0 ${spacing(1)};
`

export const meterFill = css`
  height: 100%;
  background-color: ${base_colorAction};
  border-radius: inherit;
`

export const remaining = css`
  ${font_sm};
  font-family: ${font_family_weight_medium};
  color: ${text_colorTextPrimary};
  margin: 0 0 ${spacing(1)};
`
